import { toast } from 'sonner';
import type { ReportQueryResult, ExportFormat, ExportOptions } from '@/types/custom-reports';

// ============================================================================
// EXPORT HOOKS
// ============================================================================

/**
 * Export report data to CSV
 */
async function exportToCSV(
  result: ReportQueryResult,
  options: ExportOptions
): Promise<void> {
  const { data } = result;

  // Build CSV content
  const headers = ['Label', 'Value', 'Count'];
  const rows = data.map(dp => [
    `"${dp.label}"`,
    dp.value.toFixed(2),
    dp.count || 0,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  downloadFile(csvContent, options.filename || 'report.csv', 'text/csv');
}

/**
 * Export report data to Excel (using XLSX library)
 */
async function exportToExcel(
  result: ReportQueryResult,
  options: ExportOptions
): Promise<void> {
  // Dynamically import xlsx library
  const XLSX = await import('xlsx');

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Summary sheet
  if (result.summary) {
    const summaryData = [
      ['Métrica', 'Valor'],
      ['Total', result.summary.total],
      ['Média', result.summary.average || 0],
      ['Mínimo', result.summary.min || 0],
      ['Máximo', result.summary.max || 0],
      ['Registros', result.summary.count],
    ];
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumo');
  }

  // Data sheet
  const dataRows = result.data.map(dp => ({
    'Rótulo': dp.label,
    'Valor': dp.value,
    'Quantidade': dp.count || 0,
  }));

  const dataWs = XLSX.utils.json_to_sheet(dataRows);
  XLSX.utils.book_append_sheet(wb, dataWs, 'Dados');

  // Comparison sheet if available
  if (result.comparison) {
    const comparisonData = [
      ['Período', 'Total', 'Quantidade'],
      ['Atual', result.comparison.current.total, result.comparison.current.count],
      ['Anterior', result.comparison.previous.total, result.comparison.previous.count],
      ['Variação', result.comparison.variance.toFixed(2), `${result.comparison.variancePercent.toFixed(2)}%`],
    ];
    const comparisonWs = XLSX.utils.aoa_to_sheet(comparisonData);
    XLSX.utils.book_append_sheet(wb, comparisonWs, 'Comparação');
  }

  // Generate file
  XLSX.writeFile(wb, options.filename || 'report.xlsx');
}

/**
 * Export report to PDF (using jsPDF and autotable)
 */
async function exportToPDF(
  result: ReportQueryResult,
  options: ExportOptions
): Promise<void> {
  // Dynamically import jsPDF libraries
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new jsPDF();

  // Title
  doc.setFontSize(16);
  doc.text('Relatório Financeiro', 14, 20);

  // Metadata
  if (options.includeMetadata) {
    doc.setFontSize(10);
    doc.text(`Gerado em: ${new Date(result.metadata.generatedAt).toLocaleString('pt-BR')}`, 14, 30);
    doc.text(`Fonte: ${result.metadata.dataSource.join(', ')}`, 14, 35);
  }

  let yPos = 45;

  // Summary table
  if (result.summary) {
    doc.autoTable({
      startY: yPos,
      head: [['Métrica', 'Valor']],
      body: [
        ['Total', result.summary.total.toFixed(2)],
        ['Média', (result.summary.average || 0).toFixed(2)],
        ['Mínimo', (result.summary.min || 0).toFixed(2)],
        ['Máximo', (result.summary.max || 0).toFixed(2)],
        ['Registros', result.summary.count.toString()],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // Data table
  doc.autoTable({
    startY: yPos,
    head: [['Rótulo', 'Valor', 'Quantidade']],
    body: result.data.map(dp => [
      dp.label,
      dp.value.toFixed(2),
      (dp.count || 0).toString(),
    ]),
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246] },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Comparison table
  if (result.comparison) {
    doc.autoTable({
      startY: yPos,
      head: [['Comparação de Períodos']],
      body: [
        ['Período Atual', result.comparison.current.total.toFixed(2)],
        ['Período Anterior', result.comparison.previous.total.toFixed(2)],
        ['Variação', `${result.comparison.variance >= 0 ? '+' : ''}${result.comparison.variance.toFixed(2)}`],
        ['Variação %', `${result.comparison.variancePercent >= 0 ? '+' : ''}${result.comparison.variancePercent.toFixed(2)}%`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
    });
  }

  // Save
  doc.save(options.filename || 'report.pdf');
}

/**
 * Helper function to download a file
 */
function downloadFile(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Main hook for exporting reports
 */
export function useReportExport() {
  const exportReport = async (
    result: ReportQueryResult,
    format: ExportFormat,
    options?: Partial<ExportOptions>
  ): Promise<void> => {
    const exportOptions: ExportOptions = {
      format,
      includeMetadata: true,
      filename: options?.filename || `report_${Date.now()}`,
    };

    try {
      switch (format) {
        case 'csv':
          await exportToCSV(result, exportOptions);
          toast.success('Relatório exportado para CSV!');
          break;

        case 'excel':
          await exportToExcel(result, exportOptions);
          toast.success('Relatório exportado para Excel!');
          break;

        case 'pdf':
          await exportToPDF(result, exportOptions);
          toast.success('Relatório exportado para PDF!');
          break;

        default:
          throw new Error(`Formato não suportado: ${format}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar relatório', {
        description: error instanceof Error ? error.message : 'Tente novamente.',
      });
      throw error;
    }
  };

  return { exportReport };
}

/**
 * Hook for batch export (multiple formats at once)
 */
export function useBatchExport() {
  const { exportReport } = useReportExport();

  const exportMultiple = async (
    result: ReportQueryResult,
    formats: ExportFormat[]
  ): Promise<void> => {
    for (const format of formats) {
      await exportReport(result, format);
    }
  };

  return { exportMultiple };
}
